// =============================================================================
// TenantsPage — Admin tenant (organization) management
// =============================================================================

import { useState, useCallback, type ChangeEvent } from 'react';
import { Link } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDebounce } from '@/hooks/use-debounce';
import { useAuthStore } from '@/lib/auth-store';
import {
  Card,
  CardHeader,
  Button,
  Badge,
  Input,
  Select,
  Spinner,
} from '@tkpod/ui';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TenantPlan {
  name: string;
  slug: string;
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'PENDING_VERIFICATION' | 'CANCELLED';
  plan: TenantPlan;
  membersCount: number;
  connectionsCount: number;
  createdAt: string;
}

interface TenantsResponse {
  data: Tenant[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

type StatusFilter = '' | 'ACTIVE' | 'SUSPENDED' | 'PENDING_VERIFICATION' | 'CANCELLED';
type PlanFilter = string;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAGE_LIMIT = 20;

const STATUS_CONFIG: Record<
  Tenant['status'],
  { label: string; variant: 'success' | 'danger' | 'warning' | 'default' }
> = {
  ACTIVE: { label: 'Active', variant: 'success' },
  SUSPENDED: { label: 'Suspended', variant: 'danger' },
  PENDING_VERIFICATION: { label: 'Pending', variant: 'warning' },
  CANCELLED: { label: 'Cancelled', variant: 'default' },
};

const PLAN_OPTIONS = [
  { value: '', label: 'All Plans' },
  { value: 'free', label: 'Free' },
  { value: 'starter', label: 'Starter' },
  { value: 'pro', label: 'Pro' },
  { value: 'enterprise', label: 'Enterprise' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'PENDING_VERIFICATION', label: 'Pending Verification' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const queryKeys = {
  tenants: (page: number, search: string, status: StatusFilter, plan: PlanFilter) =>
    ['admin', 'organizations', { page, search, status, plan }] as const,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TenantsPage() {
  const user = useAuthStore((s) => s.user);
  const canManageOrgs = user?.permissions?.some(
    (p) => p === 'organization:suspend' || p === 'organization:activate' || p === 'organization:manage',
  );

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [planFilter, setPlanFilter] = useState<PlanFilter>('');
  const [page, setPage] = useState(1);

  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value);
      setPage(1);
    },
    [],
  );

  const handleStatusChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      setStatusFilter(e.target.value as StatusFilter);
      setPage(1);
    },
    [],
  );

  const handlePlanChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      setPlanFilter(e.target.value);
      setPage(1);
    },
    [],
  );

  const clearFilters = useCallback(() => {
    setSearch('');
    setStatusFilter('');
    setPlanFilter('');
    setPage(1);
  }, []);

  // --- Query -----------------------------------------------------------------

  const tenantsQuery = useQuery<TenantsResponse, Error>({
    queryKey: queryKeys.tenants(page, debouncedSearch, statusFilter, planFilter),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(PAGE_LIMIT));
      if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
      if (statusFilter) params.set('status', statusFilter);
      if (planFilter) params.set('planSlug', planFilter);

      return api.get<TenantsResponse>(
        `/api/v1/admin/organizations?${params.toString()}`,
      );
    },
  });

  const queryClient = useQueryClient();

  const suspendMutation = useMutation({
    mutationFn: (orgId: string) =>
      api.post<{ message: string }>(
        `/api/v1/admin/organizations/${orgId}/suspend`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] });
      setConfirmAction(null);
    },
  });

  const activateMutation = useMutation({
    mutationFn: (orgId: string) =>
      api.post<{ message: string }>(
        `/api/v1/admin/organizations/${orgId}/activate`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'organizations'] });
      setConfirmAction(null);
    },
  });

  const [confirmAction, setConfirmAction] = useState<{
    type: 'suspend' | 'activate';
    tenant: Tenant;
  } | null>(null);

  // --- Derived ---------------------------------------------------------------

  const tenants = tenantsQuery.data?.data ?? [];
  const pagination = tenantsQuery.data?.pagination;
  const hasFilters = !!(search || statusFilter || planFilter);

  // --- Loading ---------------------------------------------------------------

  if (tenantsQuery.isLoading && !tenantsQuery.data) {
    return (
      <div className="flex items-center justify-center py-24">
        <Spinner size="lg" label="Loading tenants..." />
      </div>
    );
  }

  // --- Error -----------------------------------------------------------------

  if (tenantsQuery.isError) {
    return (
      <Card>
        <div className="flex flex-col items-center justify-center gap-4 py-16">
          <p className="text-sm text-danger">
            Failed to load tenants: {tenantsQuery.error.message}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void tenantsQuery.refetch()}
          >
            Retry
          </Button>
        </div>
      </Card>
    );
  }

  // --- Render ----------------------------------------------------------------

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Tenant Management"
          description="View and manage all organizations registered on the platform."
        />

        {/* Filters */}
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div className="w-full max-w-xs">
            <Input
              placeholder="Search by name or slug..."
              value={search}
              onChange={handleSearchChange}
            />
          </div>

          <div className="w-40">
            <Select
              options={STATUS_OPTIONS}
              value={statusFilter}
              onChange={handleStatusChange}
            />
          </div>

          <div className="w-40">
            <Select
              options={PLAN_OPTIONS}
              value={planFilter}
              onChange={handlePlanChange}
            />
          </div>

          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear Filters
            </Button>
          )}

          {tenantsQuery.isFetching && tenantsQuery.data && (
            <Spinner size="sm" />
          )}
        </div>

        {/* Table */}
        <div className="mt-4 overflow-x-auto rounded-lg border border-border">
          {tenants.length === 0 ? (
            <div className="flex items-center justify-center py-12 text-sm text-text-tertiary">
              {hasFilters
                ? 'No tenants match your filters.'
                : 'No tenants found.'}
            </div>
          ) : (
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-surface-secondary">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Organization
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Slug
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Plan
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Members
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Created
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-text-secondary">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-surface">
                {tenants.map((tenant) => {
                  const statusCfg = STATUS_CONFIG[tenant.status];

                  return (
                    <tr
                      key={tenant.id}
                      className="transition-colors hover:bg-surface-secondary"
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Link
                          to={`/tenants/${tenant.id}`}
                          className="text-sm font-medium text-brand-600 hover:text-brand-700 hover:underline"
                        >
                          {tenant.name}
                        </Link>
                      </td>

                      <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap">
                        <code className="rounded bg-surface-secondary px-1.5 py-0.5 text-xs">
                          {tenant.slug}
                        </code>
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap">
                        <Badge variant="info" size="sm">
                          {tenant.plan.name}
                        </Badge>
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap">
                        <Badge variant={statusCfg.variant} size="sm" dot>
                          {statusCfg.label}
                        </Badge>
                      </td>

                      <td className="px-4 py-3 text-sm text-text-secondary whitespace-nowrap text-center">
                        {tenant.membersCount}
                      </td>

                      <td className="px-4 py-3 text-sm text-text-tertiary whitespace-nowrap">
                        {formatDate(tenant.createdAt)}
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Link to={`/tenants/${tenant.id}`}>
                            <Button variant="outline" size="sm">
                              View
                            </Button>
                          </Link>
                          {canManageOrgs && tenant.status === 'ACTIVE' && (
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => setConfirmAction({ type: 'suspend', tenant })}
                            >
                              Suspend
                            </Button>
                          )}
                          {canManageOrgs && tenant.status === 'SUSPENDED' && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => setConfirmAction({ type: 'activate', tenant })}
                            >
                              Activate
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-text-tertiary">
              Showing{' '}
              {tenants.length === 0
                ? 0
                : (pagination.page - 1) * pagination.limit + 1}
              –{Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
              {pagination.total} tenants
            </p>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>

              <PageNumbers
                current={pagination.page}
                total={pagination.totalPages}
                onPageChange={setPage}
              />

              <Button
                variant="outline"
                size="sm"
                disabled={pagination.page >= pagination.totalPages}
                onClick={() =>
                  setPage((p) => Math.min(pagination.totalPages, p + 1))
                }
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setConfirmAction(null)}
          />
          <div className="relative z-10 w-full max-w-md rounded-lg bg-surface p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-foreground">
              {confirmAction.type === 'suspend'
                ? 'Suspend Organization'
                : 'Activate Organization'}
            </h3>
            <p className="mt-2 text-sm text-muted">
              {confirmAction.type === 'suspend'
                ? `Are you sure you want to suspend "${confirmAction.tenant.name}"? All members will lose access immediately.`
                : `Are you sure you want to activate "${confirmAction.tenant.name}"?`}
            </p>

            {(suspendMutation.error || activateMutation.error) && (
              <p className="mt-2 text-sm text-danger">
                {(suspendMutation.error || activateMutation.error)?.message ??
                  'An error occurred'}
              </p>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => setConfirmAction(null)}
                disabled={
                  suspendMutation.isPending || activateMutation.isPending
                }
              >
                Cancel
              </Button>
              <Button
                variant={
                  confirmAction.type === 'suspend' ? 'danger' : 'primary'
                }
                onClick={() => {
                  if (confirmAction.type === 'suspend') {
                    suspendMutation.mutate(confirmAction.tenant.id);
                  } else {
                    activateMutation.mutate(confirmAction.tenant.id);
                  }
                }}
                disabled={
                  suspendMutation.isPending || activateMutation.isPending
                }
              >
                {suspendMutation.isPending || activateMutation.isPending
                  ? 'Processing...'
                  : confirmAction.type === 'suspend'
                    ? 'Suspend'
                    : 'Activate'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function PageNumbers({
  current,
  total,
  onPageChange,
}: {
  current: number;
  total: number;
  onPageChange: (page: number) => void;
}) {
  if (total <= 1) return null;

  const pages: (number | 'ellipsis')[] = [];
  const addPage = (n: number) => {
    if (n >= 1 && n <= total && !pages.includes(n)) {
      pages.push(n);
    }
  };

  addPage(1);
  addPage(current - 1);
  addPage(current);
  addPage(current + 1);
  addPage(total);

  const numericPages = (
    pages.filter((p) => typeof p === 'number') as number[]
  ).sort((a, b) => a - b);

  const result: (number | 'ellipsis')[] = [];
  for (let i = 0; i < numericPages.length; i++) {
    if (i > 0 && numericPages[i] - numericPages[i - 1] > 1) {
      result.push('ellipsis');
    }
    result.push(numericPages[i]);
  }

  return (
    <div className="flex items-center gap-1">
      {result.map((item, idx) =>
        item === 'ellipsis' ? (
          <span
            key={`ellipsis-${idx}`}
            className="px-1 text-xs text-text-tertiary"
          >
            ...
          </span>
        ) : (
          <button
            key={item}
            type="button"
            onClick={() => onPageChange(item)}
            className={`flex h-8 min-w-[2rem] items-center justify-center rounded-md px-2 text-xs font-medium transition-colors ${
              item === current
                ? 'bg-brand-600 text-white'
                : 'text-text-secondary hover:bg-surface-secondary hover:text-text-primary'
            }`}
          >
            {item}
          </button>
        ),
      )}
    </div>
  );
}
